import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy, MessageCircle, Newspaper, User } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { QlashIcon } from '@/components/common/QlashIcon';
import { cn } from '@/lib/cn';

interface TabDef {
  to: string;
  key: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  hub?: boolean;
}

const tabs: readonly TabDef[] = [
  { to: '/tournaments', key: 'tournaments', Icon: Trophy },
  { to: '/news', key: 'news', Icon: Newspaper },
  { to: '/', key: 'hub', Icon: QlashIcon, hub: true },
  { to: '/chat', key: 'chat', Icon: MessageCircle },
  { to: '/profile', key: 'profile', Icon: User },
] as const;

export function TabBar() {
  const { t } = useTranslation('general');

  return (
    <nav
      className={cn(
        'sticky bottom-0 z-10 flex border-t border-border bg-bg/95 backdrop-blur',
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      {tabs.map(({ to, key, Icon, hub }) => (
        <NavLink
          key={to}
          to={to}
          end={to === '/'}
          className={({ isActive }) =>
            cn(
              'flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium uppercase tracking-wide transition-colors',
              isActive ? 'text-brand' : 'text-fg-muted',
            )
          }
        >
          {({ isActive }) =>
            hub ? (
              <>
                <span
                  className={cn(
                    '-mt-5 flex h-14 w-14 items-center justify-center rounded-full ring-4 ring-bg transition-colors',
                    isActive
                      ? 'bg-brand text-brand-fg shadow-[0_4px_16px_rgba(0,231,223,0.35)]'
                      : 'bg-[#002423] text-brand',
                  )}
                >
                  <Icon className="h-9 w-9" />
                </span>
                <span>{t(key, { defaultValue: key })}</span>
              </>
            ) : (
              <>
                <Icon aria-hidden className="h-5 w-5" />
                <span>{t(key, { defaultValue: key })}</span>
              </>
            )
          }
        </NavLink>
      ))}
    </nav>
  );
}
